import { buildDailySummaries, buildAnalysisStats } from '../domain/aggregators';
import { haversineDistanceMeters } from '../domain/distance';
import type { AnalysisResult, TimelineVisit } from '../domain/models';
import { detectTrips } from '../domain/tripDetector';

export type GeocodeProgress = {
  current: number;
  total: number;
  message: string;
};

export type GeocodeResult = {
  result: AnalysisResult;
  resolvedCount: number;
  failedCount: number;
};

type VisitCluster = {
  center: { lat: number; lng: number };
  visitIds: string[];
  totalMinutes: number;
};

type NominatimResponse = {
  name?: string;
  display_name?: string;
  address?: Record<string, string | undefined>;
};

const DEFAULT_LIMIT = 40;
const CLUSTER_RADIUS_METERS = 250;
const REQUEST_INTERVAL_MS = 1100;

export async function reverseGeocodeRepresentativeVisits(
  analysis: AnalysisResult,
  onProgress: (progress: GeocodeProgress) => void,
  limit = DEFAULT_LIMIT,
): Promise<GeocodeResult> {
  const clusters = buildVisitClusters(analysis.visits).slice(0, limit);
  const namesByVisitId = new Map<string, string>();
  let failedCount = 0;

  for (const [index, cluster] of clusters.entries()) {
    onProgress({
      current: index + 1,
      total: clusters.length,
      message: `代表地点 ${index + 1}/${clusters.length} の地名を取得しています`,
    });

    try {
      const name = await fetchPlaceName(cluster.center.lat, cluster.center.lng);
      if (name) {
        for (const visitId of cluster.visitIds) {
          namesByVisitId.set(visitId, name);
        }
      } else {
        failedCount += 1;
      }
    } catch {
      failedCount += 1;
    }

    if (index < clusters.length - 1) {
      await delay(REQUEST_INTERVAL_MS);
    }
  }

  return {
    result: applyVisitNames(analysis, namesByVisitId),
    resolvedCount: namesByVisitId.size,
    failedCount,
  };
}

function buildVisitClusters(visits: TimelineVisit[]): VisitCluster[] {
  const clusters: VisitCluster[] = [];

  for (const visit of visits) {
    const existing = clusters.find((cluster) => haversineDistanceMeters(cluster.center, visit.center) <= CLUSTER_RADIUS_METERS);
    if (existing) {
      const weight = existing.totalMinutes + Math.max(1, visit.durationMinutes);
      existing.center = {
        lat: (existing.center.lat * existing.totalMinutes + visit.center.lat * Math.max(1, visit.durationMinutes)) / weight,
        lng: (existing.center.lng * existing.totalMinutes + visit.center.lng * Math.max(1, visit.durationMinutes)) / weight,
      };
      existing.totalMinutes = weight;
      existing.visitIds.push(visit.id);
    } else {
      clusters.push({
        center: visit.center,
        visitIds: [visit.id],
        totalMinutes: Math.max(1, visit.durationMinutes),
      });
    }
  }

  return clusters.sort((a, b) => b.totalMinutes - a.totalMinutes);
}

async function fetchPlaceName(lat: number, lng: number): Promise<string | null> {
  const url = new URL('https://nominatim.openstreetmap.org/reverse');
  url.searchParams.set('format', 'jsonv2');
  url.searchParams.set('lat', String(lat));
  url.searchParams.set('lon', String(lng));
  url.searchParams.set('zoom', '16');
  url.searchParams.set('addressdetails', '1');
  url.searchParams.set('accept-language', 'ja');

  const response = await fetch(url.toString(), {
    headers: {
      Accept: 'application/json',
    },
  });
  if (!response.ok) return null;

  const payload = (await response.json()) as NominatimResponse;
  return formatPlaceName(payload);
}

function formatPlaceName(payload: NominatimResponse): string | null {
  const address = payload.address ?? {};
  const primary =
    payload.name ??
    address.amenity ??
    address.tourism ??
    address.shop ??
    address.building ??
    address.road ??
    address.neighbourhood ??
    address.suburb ??
    address.city ??
    address.town ??
    address.village;
  const locality = address.city ?? address.town ?? address.village ?? address.suburb ?? address.county;
  const state = address.state ?? address.province;

  const parts = [primary, locality, state].filter((part, index, array): part is string => Boolean(part) && array.indexOf(part) === index);
  if (parts.length > 0) return parts.slice(0, 3).join(' / ');

  return payload.display_name?.split(',').slice(0, 3).map((part) => part.trim()).join(' / ') || null;
}

function applyVisitNames(analysis: AnalysisResult, namesByVisitId: Map<string, string>): AnalysisResult {
  const visits = analysis.visits.map((visit) => {
    const placeName = namesByVisitId.get(visit.id);
    return placeName ? { ...visit, placeName } : visit;
  });
  const dailySummaries = buildDailySummaries(visits, analysis.segments);
  return {
    ...analysis,
    visits,
    dailySummaries,
    trips: detectTrips(dailySummaries, visits),
    stats: buildAnalysisStats(analysis.points, visits, analysis.segments),
  };
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}
